import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from 'ddc-classnames-js';
import useGoogleMap from '../../hooks/useGoogleMap';
import {
	DEFAULT_MAP_ZOOM,
	MAP_MIN_HEIGHT,
	MAP_BORDER_RADIUS
} from '../../constants/mapConstants';

const MapSection = ({ center, zoom = DEFAULT_MAP_ZOOM, locations = [] }) => {
	const { mapRef, isLoaded, loadError, handleMapResize } = useGoogleMap({
		center,
		zoom,
		locations
	});

	/**
	 * Trigger map resize when component mounts or becomes visible
	 */
	useEffect(() => {
		handleMapResize();
	}, [handleMapResize]);

	const mapContentClassNames = setClassNames([
		'map-content',
		'w-100',
		'h-100',
		'border',
		'border-2',
		'border-light',
		'rounded',
		'bg-light',
		'd-flex',
		'align-items-center',
		'justify-content-center',
		'position-relative'
	]);

	// Determine content to render
	let mapContent;
	if (loadError) {
		mapContent = (
			<div className="alert alert-danger">Error loading maps</div>
		);
	} else if (!isLoaded) {
		mapContent = (
			<div className="spinner-border" role="status">
				<span className="visually-hidden">Loading map...</span>
			</div>
		);
	} else {
		mapContent = (
			<>
				<div
					ref={mapRef}
					role="application"
					aria-label="Interactive map showing location results"
					aria-describedby="map-instructions"
					style={{
						width: '100%',
						height: '100%',
						minHeight: MAP_MIN_HEIGHT,
						borderRadius: MAP_BORDER_RADIUS,
						overflow: 'hidden'
					}}
				/>
			</>
		);
	}

	/**
	 * Map container - render map with loading/error states
	 */
	return (
		<div className="map-column flex-shrink-0 d-none d-lg-block">
			<div
				className="map-sticky-container position-sticky w-100 ps-5"
				aria-label="Interactive map of locations"
			>
				<div
					className={mapContentClassNames}
					data-testid="map-container-wrapper"
				>
					{mapContent}
				</div>
			</div>
		</div>
	);
};

MapSection.propTypes = {
	center: PropTypes.shape({
		lat: PropTypes.number.isRequired,
		lng: PropTypes.number.isRequired
	}).isRequired,
	zoom: PropTypes.number,
	locations: PropTypes.arrayOf(
		PropTypes.shape({
			latitude: PropTypes.number,
			longitude: PropTypes.number,
			name: PropTypes.string
		})
	)
};

export default MapSection;
